import { mdiClose } from '@mdi/js';
import { fetchNgwLayerItem } from '@nextgis/ngw-kit';
import { Component, Prop, Vue } from 'vue-property-decorator';

import { connector } from '../../services/connector';
import { webMapModule } from '../../store/webMap/webMap';

import type {
  FeatureItemAttachment,
  FeatureLayerField,
  NgwDateFormat,
  NgwTimeFormat,
  RequestItemsParams,
  ResourceItem,
  VectorFieldDatatype,
} from '@nextgis/ngw-connector';
import type { Feature } from 'geojson';

type ImageAttachment = FeatureItemAttachment & { imageUrl?: string };

@Component
export default class PopupContent extends Vue {
  @Prop({ type: Object }) readonly feature!: Feature;
  @Prop({ type: Object }) readonly item!: ResourceItem;

  fields: FeatureLayerField[] = [];
  attachments: ImageAttachment[] = [];
  imagePreviewHeight = 200;
  imagePreviewWidth = 200;

  icon = { close: mdiClose };

  activeAttachment: ImageAttachment | null = null;
  loadingActiveAttachment = false;

  private abortControllers: AbortController[] = [];

  get dialog(): boolean {
    return !!this.activeAttachment;
  }
  set dialog(val: boolean) {
    if (!val) {
      this.activeAttachment = null;
    }
  }

  get tab(): string | null {
    return webMapModule.activePopupTab;
  }
  set tab(val: string | null) {
    webMapModule.setActivePopupTab(val);
  }

  mounted() {
    if (this.item.feature_layer) {
      this.fields = this.item.feature_layer.fields.filter((f) => {
        return f.grid_visibility;
      });
      this.loadFeatureItem();
    }
  }

  unmount() {
    this.abort();
  }

  abort() {
    for (const a of this.abortControllers) {
      a.abort();
    }
    this.abortControllers = [];
  }

  getValue(field: string) {
    const props = this.feature.properties;

    if (props) {
      const val = props[field];
      if (val !== undefined) {
        const parseDate = (v: NgwDateFormat): string => {
          const { year, month, day } = v;
          return [day, month, year]
            .map((x) => String(x).padStart(2, '0'))
            .join('.');
        };
        const parseTime = (v: NgwTimeFormat): string => {
          const { hour, minute, second } = v;
          return [hour, minute, second]
            .map((x) => String(x).padStart(2, '0'))
            .join(':');
        };

        const resourceField = this.fields.find((f) => f.keyname === field);
        const dataType: VectorFieldDatatype = resourceField
          ? resourceField.datatype
          : 'STRING';
        if (dataType === 'DATE') {
          return parseDate(val);
        } else if (dataType === 'TIME') {
          return parseTime(val);
        } else if (dataType === 'DATETIME') {
          return `${parseDate(val)} ${parseTime(val)}`;
        }
        return val;
      }
    }
    return '';
  }

  async loadFeatureItem() {
    const featureId = Number(this.feature.id);
    const resourceId = this.item.resource.id;

    const featureItem = await fetchNgwLayerItem({
      connector,
      featureId,
      resourceId,
      cache: true,
      extensions: ['attachment', 'description'],
    });

    if (featureItem) {
      const attachments = featureItem.extensions.attachment;
      const srcList: ImageAttachment[] = [];
      if (attachments) {
        for (const a of attachments) {
          const attachment = await this._loadAttachment(a, [
            this.imagePreviewWidth,
            this.imagePreviewHeight,
          ]);
          srcList.push(attachment);
        }
        this.attachments = srcList;
      }
    }
  }

  async openAttachment(attachment: ImageAttachment) {
    this.loadingActiveAttachment = true;
    try {
      const activeAttachment = await this._loadAttachment(attachment);
      this.activeAttachment = activeAttachment;
    } finally {
      this.loadingActiveAttachment = false;
    }
  }

  private async _loadAttachment(
    attachment: ImageAttachment,
    size?: number[],
  ): Promise<ImageAttachment> {
    // this.abort();
    const featureId = Number(this.feature.id);
    const resourceId = this.item.resource.id;

    const props: RequestItemsParams<'feature_attachment.image'> = {
      id: resourceId,
      fid: featureId,
      aid: attachment.id,
    };
    if (size) {
      props.size = `${size[0]}x${size[1]}`;
    }

    const src = await connector.get(
      'feature_attachment.image',
      { responseType: 'blob', signal: this._makeAbortSignal() },
      props,
    );
    const urlCreator = window.URL || window.webkitURL;
    const imageUrl = urlCreator.createObjectURL(src);
    return { ...attachment, imageUrl };
  }

  private _makeAbortSignal() {
    const abortController = new AbortController();
    this.abortControllers.push(abortController);
    return abortController.signal;
  }
}
